import React, { FormEvent, ReactElement, useCallback } from "react";
import classnames from "classnames";

import "../../../css/buttons/toggle_button_group.css";
import { Props as ToggleButtonProps } from "./toggle_button";

export type Props<Value = any> = {
  className?: string
  /**
   * Callback fired when the value changes.
   *
   * @param event The event source of the callback.
   * @param value of the selected button.
   */
  onChange?: (event: FormEvent<HTMLButtonElement>, value: Value) => void
  /** The currently selected value within the group. */
  value?: Value
  size?: "small" | "medium" | "large"
  children: ReactElement<ToggleButtonProps>[]
}

const ToggleButtonGroup = React.forwardRef<HTMLDivElement, Props>(function ToggleButtonGroup(props, ref) {
  const {
    children,
    className,
    onChange,
    value,
    ...other
  } = props;

  const handleChange = useCallback((event, buttonValue) => {
    if (onChange) {
      onChange(event, buttonValue);
    }
  }, [onChange]);

  return <div className={classnames(props.className, "ToggleButtonGroup")} ref={ref} role="group" {...other}>
    {React.Children.map(children as ReactElement<ToggleButtonProps>[], child => {
      if (React.isValidElement(child)) {
        const buttonValue = child.props.value;
        const selected = value === buttonValue;

        return React.cloneElement(child, {
          className: classnames("ToggleButtonGroup-grouped",
            child.props.className
          ),
          selected,
          onChange: handleChange,
        });
      } else {
        return null;
      }
    })}
  </div>;
});

export default React.memo(ToggleButtonGroup);
